import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import {
  streamFlowApi,
  type Household,
  getActiveHouseholdId,
  setActiveHouseholdId,
} from '@lib/streamFlowApi';

/**
 * Multi-tenant household context. Loads the user's households on auth,
 * persists the chosen one in localStorage (read by streamFlowApi to pin
 * the X-Household-Id header on every request), and exposes a switcher.
 *
 * Auto-pick rules:
 *   - 0 households → activeId stays null. The dashboard surfaces a
 *     "Create your household" empty state instead of throwing.
 *   - 1 household → activeId = that one (no UI prompt needed).
 *   - >1 households → use the persisted choice, falling back to the
 *     first owned household, falling back to the first listed.
 */

interface HouseholdContextValue {
  households: Household[];
  activeId: string | null;
  activeHousehold: Household | null;
  loading: boolean;
  /** True until the first listHouseholds call settles (success or fail).
   * Components that need to gate first paint on a household pick should
   * read this rather than `loading` (which retoggles on every refresh). */
  ready: boolean;
  /** Captures a listHouseholds failure so the UI can distinguish
   * "user genuinely has no households" (households=[] AND fetchError=null
   * AND ready=true) from "fetch failed and we don't know what they have"
   * (households=[] AND fetchError set). Without this, a transient network
   * blip after switching apiBase silently dumps the user on the
   * create-household form even when they have valid households. */
  fetchError: { code: string; message: string } | null;
  setActiveId: (id: string) => void;
  refresh: () => Promise<void>;
  createHousehold: (name: string) => Promise<{ ok: true; id: string } | { ok: false; error: string }>;
}

const HouseholdContext = createContext<HouseholdContextValue | undefined>(undefined);

export function HouseholdProvider({ children }: { children: ReactNode }) {
  const { user, loading: authLoading } = useAuth();
  const [households, setHouseholds] = useState<Household[]>([]);
  const [activeId, setActiveIdState] = useState<string | null>(() => getActiveHouseholdId());
  const [loading, setLoading] = useState(false);
  const [ready, setReady] = useState(false);
  const [fetchError, setFetchError] = useState<{ code: string; message: string } | null>(null);
  // Guard against late-resolving fetches stomping on a newer state when
  // the user signs out / signs in rapidly.
  const generationRef = useRef(0);

  const refresh = useCallback(async () => {
    if (!user) return;
    const myGen = ++generationRef.current;
    setLoading(true);
    try {
      const res = await streamFlowApi.listHouseholds();
      if (myGen !== generationRef.current) return;
      if (res.success && res.data) {
        setFetchError(null);
        setHouseholds(res.data);
        // Reconcile the persisted activeId with what's actually available.
        const persisted = getActiveHouseholdId();
        const stillValid = persisted && res.data.some((h) => h.id === persisted);
        if (stillValid) {
          setActiveIdState(persisted);
        } else if (res.data.length > 0) {
          const owned = res.data.find((h) => h.role === 'owner');
          const pick = (owned ?? res.data[0]).id;
          setActiveHouseholdId(pick);
          setActiveIdState(pick);
        } else {
          setActiveHouseholdId(null);
          setActiveIdState(null);
        }
      } else {
        // Critical: do NOT clear households on failure. A network blip
        // after switching apiBase shouldn't trick the gate into showing
        // the create-household form when the user already has one. Keep
        // any previously-loaded list and surface the error instead.
        setFetchError(res.error ?? { code: 'UNKNOWN', message: 'Failed to load households' });
      }
    } finally {
      if (myGen === generationRef.current) {
        setLoading(false);
        setReady(true);
      }
    }
  }, [user]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setHouseholds([]);
      setActiveIdState(null);
      setActiveHouseholdId(null);
      setReady(true);
      return;
    }
    void refresh();
  }, [authLoading, user, refresh]);

  const setActiveId = useCallback((id: string) => {
    setActiveHouseholdId(id);
    setActiveIdState(id);
  }, []);

  const createHousehold = useCallback(
    async (name: string): Promise<{ ok: true; id: string } | { ok: false; error: string }> => {
      const res = await streamFlowApi.createHousehold(name);
      if (!res.success || !res.data) {
        return { ok: false, error: res.error?.message ?? 'Failed to create household' };
      }
      const created = res.data;
      setHouseholds((prev) => [...prev, created]);
      setActiveHouseholdId(created.id);
      setActiveIdState(created.id);
      return { ok: true, id: created.id };
    },
    [],
  );

  const activeHousehold = useMemo(
    () => households.find((h) => h.id === activeId) ?? null,
    [households, activeId],
  );

  return (
    <HouseholdContext.Provider
      value={{
        households,
        activeId,
        activeHousehold,
        loading,
        ready,
        fetchError,
        setActiveId,
        refresh,
        createHousehold,
      }}
    >
      {children}
    </HouseholdContext.Provider>
  );
}

export function useHousehold() {
  const ctx = useContext(HouseholdContext);
  if (!ctx) throw new Error('useHousehold must be used within HouseholdProvider');
  return ctx;
}
